import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IGetApplicationVersionCommentsQueryParams } from '~/api/application-versions'
import type { IApplicationVersion, IApplicationVersionForListing, IVersionComment } from '~/types/application-version'
import type { DeviceType } from '~/types/device-type'
import type { IAppVersion } from '~/types/version'

import {
  getApplicationVersions,
  getApplicationVersion,
  getAppDetailsVersionPerId,
  getAppVersions,
  getApplicationVersionComments,
  setAppVersionNote,
  setAppVersionComments,
  deleteAppVersionComment,
} from '~/api/application-versions'

////////// KEYS //////////

export const keys = {
  all: ['versions'] as const,
  comments: (params: { deviceType?: DeviceType; externalId?: string; semanticVersion?: string }) =>
    [...keys.all, 'comments', params] as const,
  details: (versionId: string) => [...keys.all, 'details', versionId] as const,
  detailsV2: (params: {
    country?: string
    deviceType?: DeviceType
    externalId?: string
    lang?: string
    semanticVersion?: string
  }) => [...keys.all, 'details', params] as const,
  list: (appId?: string) => [...keys.all, 'list', appId] as const,
  listV2: (params?: { deviceType?: DeviceType; externalId?: string; limit?: number; page?: number; q?: string }) =>
    [...keys.all, 'list', params] as const,
}

////////// QUERIES //////////

/** @deprecated Use useVersionsListQueryV2 instead */
export const useVersionsListQuery = (
  params: { appId?: string },
  options: { enabled?: boolean }
): UseQueryResult<IAppVersion[]> => {
  return useQuery({
    queryFn: () => getAppVersions(params.appId),
    queryKey: keys.list(params.appId),
    ...options,
  })
}

/**
 * Get the application versions by store ID & device type
 * @param deviceType
 * @param externalId
 * @returns A paginated response of application versions
 */
export const useVersionsListQueryV2 = ({
  deviceType,
  externalId,
  ...queryParams
}: {
  deviceType?: DeviceType
  externalId?: string
  limit?: number
  page?: number
  q?: string
}): UseQueryResult<IPaginatedResponse<IApplicationVersionForListing>> => {
  return useQuery({
    queryFn:
      externalId && deviceType ? () => getApplicationVersions({ deviceType, externalId, ...queryParams }) : skipToken,
    queryKey: keys.listV2({ deviceType, externalId, ...queryParams }),
  })
}

/**
 * Get an application version by device type, external ID, and semantic version
 * @returns
 */
export const useVersionQuery = ({
  deviceType,
  externalId,
  semanticVersion,
  ...queryParams
}: {
  country?: string
  deviceType?: DeviceType
  externalId?: string
  lang?: string
  semanticVersion?: string
}): UseQueryResult<IApplicationVersion> => {
  return useQuery({
    queryFn:
      deviceType && externalId && semanticVersion
        ? () => getApplicationVersion({ deviceType, externalId, semanticVersion, ...queryParams })
        : skipToken,
    queryKey: keys.detailsV2({ deviceType, externalId, semanticVersion, ...queryParams }),
  })
}

/** @doc https://store-dev-europe-west1-molccirxma-ew.a.run.app/#get-/application-versions/device-type/-deviceType-/external-id/-externalID-/semantic-version/-semanticVersion-/comments */
export const useVersionCommentsQuery = ({
  deviceType,
  externalId,
  semanticVersion,
  ...queryParams
}: {
  deviceType?: DeviceType
  externalId?: string
  semanticVersion?: string
} & IGetApplicationVersionCommentsQueryParams): UseQueryResult<IPaginatedResponse<IVersionComment>> => {
  return useQuery({
    queryFn:
      deviceType && externalId && semanticVersion
        ? () => getApplicationVersionComments({ deviceType, externalId, semanticVersion, ...queryParams })
        : skipToken,
    queryKey: keys.comments({ deviceType, externalId, semanticVersion, ...queryParams }),
  })
}

/** @deprecated Use useVersionQuery instead */
export const useVersionsDetailsQuery = (
  {
    appId,
    versionId,
  }: {
    appId?: string
    versionId?: string
  },
  options: {
    enabled?: boolean
  },
  queryFn?: (versionId?: string) => Promise<any>
): UseQueryResult<IAppVersion> => {
  return useQuery({
    queryFn: queryFn
      ? () => queryFn(versionId)
      : appId && versionId
        ? () => getAppDetailsVersionPerId({ appId, versionId })
        : skipToken,
    queryKey: versionId ? keys.details(versionId) : [],
    ...options,
  })
}

/**Mutations */

export const useSetAppVersionNoteMutation = (): UseMutationResult<
  void,
  unknown,
  {
    content: string
    deviceType: DeviceType
    externalId: string
    fileIds: string[]
    semanticVersion: string
  },
  unknown
> => {
  return useMutation({ mutationFn: setAppVersionNote })
}

export const useSetAppVersionCommentsMutation = (): UseMutationResult<
  void,
  unknown,
  {
    content: string
    deviceType: DeviceType
    externalId: string
    fileIds: string[]
    parentCommentId?: string
    semanticVersion: string
  },
  unknown
> => {
  return useMutation({ mutationFn: setAppVersionComments })
}

export const useDeleteAppVersionComment = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: deleteAppVersionComment })
}
